import { RefObject, useCallback } from 'react';

import { DEFAULT_MONTHS_COUNT } from '@calendar/calendarHelpers';
import useMonthsToRenderStore from '@calendar/store/useMonthsToRenderStore';

type HandleMonthChange = (
  index: number,
  withScroll?: boolean,
  monthListRef?: RefObject<HTMLDivElement>,
) => Promise<void>;

const useHandleMonthChange = () => {
  const { monthsToRender, setMonthsToRender, setSelectedMonth } =
    useMonthsToRenderStore();

  return useCallback<HandleMonthChange>(
    async (index, withScroll, monthListRef) => {
      setMonthsToRender(
        index <= DEFAULT_MONTHS_COUNT ? index : DEFAULT_MONTHS_COUNT - 1,
        !withScroll,
      );
      setSelectedMonth(monthsToRender[withScroll ? index : index - 1]);

      if (withScroll) {
        const { ref } = monthsToRender[index];

        if (!ref.current) {
          await new Promise<void>((resolve) => {
            const observer = new MutationObserver((mutations) => {
              const { target } = mutations[0];

              if (target.contains(ref.current)) {
                observer.disconnect();
                resolve();
              }
            });

            observer.observe(document.body, {
              childList: true,
              subtree: true,
            });
          });
        }

        const targetRef =
          index === 0 ? monthListRef?.current?.parentElement : ref.current;

        targetRef?.scrollIntoView({
          behavior: 'auto',
          block: 'start',
          inline: 'start',
        });
      }
    },
    [monthsToRender, setMonthsToRender, setSelectedMonth],
  );
};

export default useHandleMonthChange;
